import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Button,
  Table,
  Tag,
  Space,
  message,
  Skeleton,
  Modal,
  Select,
  Typography,
  Alert,
} from 'antd';
import {
  ReloadOutlined,
  ThunderboltOutlined,
  StopOutlined,
  WalletOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import KPICard from '../dashboard/KPICard';
import {
  fetchAdsOpsSummary,
  runAdsRankingPrecompute,
  runAdsCloseExpired,
  fetchShadowbannedUsers,
  fetchAdWalletTransactions,
  patchUserShadowban,
  type AdsOpsSummary,
  type ShadowbannedUser,
  type AdWalletTransaction,
} from '../../services/adsService';
import { formatVnDate } from '../../utils/vnDateTime';

const ROUNDED: React.CSSProperties = {
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const TX_TYPE_LABELS: Record<string, string> = {
  ADS_HOLD: 'Giữ điểm (Hold)',
  ADS_SETTLE: 'Thanh toán (Settle)',
  ADS_REFUND: 'Hoàn điểm (Refund)',
};

const TX_TYPE_COLORS: Record<string, string> = {
  ADS_HOLD: 'orange',
  ADS_SETTLE: 'green',
  ADS_REFUND: 'blue',
};

export const AdsOpsTab: React.FC = () => {
  const [summary, setSummary] = useState<AdsOpsSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [opsLoading, setOpsLoading] = useState(false);

  const [shadowUsers, setShadowUsers] = useState<ShadowbannedUser[]>([]);
  const [shadowTotal, setShadowTotal] = useState(0);
  const [shadowPage, setShadowPage] = useState(1);
  const [shadowLoading, setShadowLoading] = useState(false);

  const [walletTx, setWalletTx] = useState<AdWalletTransaction[]>([]);
  const [walletTotal, setWalletTotal] = useState(0);
  const [walletPage, setWalletPage] = useState(1);
  const [walletType, setWalletType] = useState<string | undefined>();
  const [walletLoading, setWalletLoading] = useState(false);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const data = await fetchAdsOpsSummary();
      setSummary(data);
    } catch {
      message.error('Không tải được dữ liệu vận hành quảng cáo');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadShadowUsers = useCallback(async () => {
    setShadowLoading(true);
    try {
      const { users, total } = await fetchShadowbannedUsers({
        page: shadowPage,
        pageSize: 10,
      });
      setShadowUsers(users);
      setShadowTotal(total);
    } catch {
      message.error('Không tải được danh sách shadowban');
    } finally {
      setShadowLoading(false);
    }
  }, [shadowPage]);

  const loadWalletTx = useCallback(async () => {
    setWalletLoading(true);
    try {
      const { transactions, total } = await fetchAdWalletTransactions({
        page: walletPage,
        pageSize: 15,
        type: walletType,
      });
      setWalletTx(transactions);
      setWalletTotal(total);
    } catch {
      message.error('Không tải được giao dịch ví quảng cáo');
    } finally {
      setWalletLoading(false);
    }
  }, [walletPage, walletType]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadShadowUsers();
  }, [loadShadowUsers]);

  useEffect(() => {
    loadWalletTx();
  }, [loadWalletTx]);

  const handlePrecompute = () => {
    Modal.confirm({
      title: 'Chạy precompute ranking?',
      content:
        'Hệ thống sẽ tính lại BaseScore và cập nhật Redis (ads:active, ads:geo). Thường chạy tự động mỗi 30 phút.',
      okText: 'Chạy ngay',
      cancelText: 'Huỷ',
      onOk: async () => {
        setOpsLoading(true);
        try {
          const res = await runAdsRankingPrecompute();
          if (res.success) {
            message.success(res.message ?? 'Precompute thành công');
            loadSummary();
          } else {
            message.error('Precompute thất bại');
          }
        } catch {
          message.error('Precompute thất bại');
        } finally {
          setOpsLoading(false);
        }
      },
    });
  };

  const handleCloseExpired = () => {
    Modal.confirm({
      title: 'Đóng quảng cáo hết hạn?',
      okText: 'Đóng',
      cancelText: 'Huỷ',
      onOk: async () => {
        setOpsLoading(true);
        try {
          const res = await runAdsCloseExpired();
          if (res.success) {
            message.success(res.message ?? 'Đã đóng quảng cáo hết hạn');
          }
        } catch {
          message.error('Thao tác thất bại');
        } finally {
          setOpsLoading(false);
        }
      },
    });
  };

  const handleUnshadowban = (user: ShadowbannedUser) => {
    Modal.confirm({
      title: `Gỡ shadowban cho ${user.name}?`,
      content: 'Người dùng sẽ thấy lại quảng cáo sponsored.',
      okText: 'Gỡ shadowban',
      cancelText: 'Huỷ',
      onOk: async () => {
        try {
          const res = await patchUserShadowban(user.id, false);
          if (res.success) {
            message.success('Đã gỡ shadowban');
            loadShadowUsers();
            loadSummary();
          }
        } catch {
          message.error('Không thể gỡ shadowban');
        }
      },
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Space wrap>
        <Button
          type="primary"
          icon={<ThunderboltOutlined />}
          loading={opsLoading}
          onClick={handlePrecompute}
        >
          Precompute ranking (Redis)
        </Button>
        <Button
          icon={<StopOutlined />}
          loading={opsLoading}
          onClick={handleCloseExpired}
        >
          Đóng QC hết hạn
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            loadSummary();
            loadShadowUsers();
            loadWalletTx();
          }}
        >
          Làm mới
        </Button>
      </Space>

      {!summary?.redis.redisAvailable && (
        <Alert
          type="warning"
          showIcon
          message="Redis không khả dụng — ranking realtime và chống gian lận có thể bị ảnh hưởng."
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          {summaryLoading ? (
            <Skeleton.Input active style={{ width: '100%', height: 100 }} />
          ) : (
            <KPICard
              title="QC trong Redis"
              value={summary?.redis.activeAdsCount ?? 0}
              suffix="merchant"
            />
          )}
        </Col>
        <Col xs={24} sm={12} lg={6}>
          {summaryLoading ? (
            <Skeleton.Input active style={{ width: '100%', height: 100 }} />
          ) : (
            <KPICard
              title="Điểm đang giữ (Hold)"
              value={summary?.wallet.totalHeldPoints ?? 0}
              suffix="điểm"
              prefix={<WalletOutlined />}
            />
          )}
        </Col>
        <Col xs={24} sm={12} lg={6}>
          {summaryLoading ? (
            <Skeleton.Input active style={{ width: '100%', height: 100 }} />
          ) : (
            <KPICard
              title="User shadowban"
              value={summary?.fraud.shadowbannedUsers ?? 0}
              prefix={<SafetyOutlined />}
            />
          )}
        </Col>
        <Col xs={24} sm={12} lg={6}>
          {summaryLoading ? (
            <Skeleton.Input active style={{ width: '100%', height: 100 }} />
          ) : (
            <KPICard
              title="Huỷ đơn QC (24h)"
              value={summary?.fraud.adOrderCancellations24h ?? 0}
              suffix="đơn"
            />
          )}
        </Col>
      </Row>

      <Card title="Cấu hình CPA Ranking" style={ROUNDED}>
        {summaryLoading || !summary ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : (
          <Row gutter={[24, 12]}>
            <Col span={12}>
              <Typography.Text type="secondary">Trọng số W1–W4</Typography.Text>
              <div>
                Bid {summary.ranking.weights.w1} · CVR{' '}
                {summary.ranking.weights.w2} · Rating{' '}
                {summary.ranking.weights.w3} · Proximity{' '}
                {summary.ranking.weights.w4}
              </div>
            </Col>
            <Col span={12}>
              <Typography.Text type="secondary">
                Epsilon-greedy (cold start)
              </Typography.Text>
              <div>
                {(summary.ranking.epsilon * 100).toFixed(0)}% exploration
              </div>
            </Col>
            <Col span={12}>
              <Typography.Text type="secondary">
                Bán kính tối đa / Top N
              </Typography.Text>
              <div>
                {summary.ranking.maxRadiusM}m · Top{' '}
                {summary.ranking.nearbyLimit}
              </div>
            </Col>
            <Col span={12}>
              <Typography.Text type="secondary">
                Cron precompute
              </Typography.Text>
              <div>{summary.ranking.precomputeCron}</div>
            </Col>
            <Col span={12}>
              <Typography.Text type="secondary">
                Ngưỡng huỷ đơn QC → shadowban
              </Typography.Text>
              <div>{summary.fraud.adCancelThreshold} lần / ngày</div>
            </Col>
            <Col span={12}>
              <Typography.Text type="secondary">
                Giao dịch ví QC (24h)
              </Typography.Text>
              <div>
                Hold {summary.wallet.transactions24h.hold} · Settle{' '}
                {summary.wallet.transactions24h.settle} · Refund{' '}
                {summary.wallet.transactions24h.refund}
              </div>
            </Col>
          </Row>
        )}
      </Card>

      <Card title="Người dùng bị ẩn quảng cáo (shadowban)" style={ROUNDED}>
        <Table<ShadowbannedUser>
          rowKey="id"
          loading={shadowLoading}
          dataSource={shadowUsers}
          pagination={{
            current: shadowPage,
            pageSize: 10,
            total: shadowTotal,
            onChange: setShadowPage,
            showSizeChanger: false,
          }}
          columns={[
            {
              title: 'Người dùng',
              render: (_, r) => (
                <Link to={`/user-management/${r.id}`}>{r.name}</Link>
              ),
            },
            { title: 'Email', dataIndex: 'email', render: v => v || '–' },
            { title: 'SĐT', dataIndex: 'phone', render: v => v || '–' },
            {
              title: 'Trạng thái',
              render: () => <Tag color="red">Shadowban</Tag>,
            },
            {
              title: '',
              width: 140,
              render: (_, r) => (
                <Button size="small" onClick={() => handleUnshadowban(r)}>
                  Gỡ shadowban
                </Button>
              ),
            },
          ]}
        />
      </Card>

      <Card
        title="Giao dịch ví quảng cáo (Hold / Settle / Refund)"
        style={ROUNDED}
        extra={
          <Select
            allowClear
            placeholder="Loại GD"
            style={{ width: 180 }}
            value={walletType}
            onChange={v => {
              setWalletType(v);
              setWalletPage(1);
            }}
            options={[
              { value: 'ADS_HOLD', label: 'Hold' },
              { value: 'ADS_SETTLE', label: 'Settle' },
              { value: 'ADS_REFUND', label: 'Refund' },
            ]}
          />
        }
      >
        <Table<AdWalletTransaction>
          rowKey="id"
          loading={walletLoading}
          dataSource={walletTx}
          pagination={{
            current: walletPage,
            pageSize: 15,
            total: walletTotal,
            onChange: setWalletPage,
            showSizeChanger: false,
          }}
          columns={[
            {
              title: 'Loại',
              dataIndex: 'type',
              render: (t: string) => (
                <Tag color={TX_TYPE_COLORS[t] ?? 'default'}>
                  {TX_TYPE_LABELS[t] ?? t}
                </Tag>
              ),
            },
            {
              title: 'Merchant',
              render: (_, r) => (
                <span>{r.merchant_name ?? `#${r.merchant_id}`}</span>
              ),
            },
            {
              title: 'Số điểm',
              dataIndex: 'amount_gcoin',
              render: v => `${v} điểm`,
            },
            {
              title: 'Order',
              dataIndex: 'reference_id',
              render: v => (v ? `#${v}` : '–'),
            },
            {
              title: 'Thời gian',
              dataIndex: 'created_at',
              render: v => formatVnDate(v, 'D MMM YYYY, HH:mm'),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default AdsOpsTab;
